<template>
  <div class="cashflow-type-card">
    <card :data-cy="cardTypeId"
          :has-footer-padding="false" class="card-wrapper"
          :class="{'out-of-date': outOfDate }"
          :on-menu-opened="sectionMenuOpened">
      <template v-slot:default>
        <cashflow-type-details  :editable-category="categoryForActions"
                                :show-expense-progress="!!subTree"
                                :card-title="cardTitle"
                                :sub-tree="subTree"
                                :is-income="isIncome"
                                :cashflow-category="cashflowCategory"
                                :previous-month="previousMonth"
                                :recommended-label="recommendedLabel"
                                :up-to-now-label="upToNowLabel"
                                :blocked="blocked"
                                :out-of-date="outOfDate"
                                :show-warning-if-exceeding-budget="showWarningIfExceedingBudget"/>
      </template>
      <template v-slot:menuItems v-if="hasMenu">
        <cashflow-income-section-menu v-if="shouldDisplayIncomeMenu
"/>
        <cashflow-category-section-menu v-else
                                        :show-info-popups="shouldShowInfoMenuItem"
                                        :show-category-trends="shouldShowTrendsMenuItem"
                                        :card-type-id="cardTypeId"
                                        :sub-tree="subTree"
                                        :can-edit="isEditableCategory"
                                        :category="categoryForActions"/>
      </template>
      <template v-slot:footer v-if="!blocked">
        <slot name="bottom" v-if="hasBottomSlot"></slot>
      </template>
    </card>
    <Icon class="card-update-indicator" :class="{visible: showUpdateIndicator}" icon-name="square" fill
          :color="iconColor"/>
  </div>
</template>

<script>
import _ from 'lodash';
import BaseUI from 'base-ui';
import Segment from '@/Segment';
import { mapGetters } from 'vuex';
import EventBus from '@/event-bus/event-bus';
import cashflowViewConsts from '@/constants/cashflow-view';
import CashflowCategorySectionMenu
  from '../../../current-month/components/cashflow-edit/edit-reccuring/CashflowCategorySectionMenu.vue';
import CashflowIncomeSectionMenu
  from '../../../current-month/components/cashflow-edit/edit-reccuring/CashflowIncomeSectionMenu.vue';
import CashflowTypeDetails from './CashflowTypeDetails';
import InfoPopups from '../../../info-popups/InfoPopups';
import CashflowTrends from '../../../cashflow-trend/CashflowTrendsMixin';

export default {
  name: 'CashflowTypeCard',
  components: {
    Card: BaseUI.Card,
    Icon: BaseUI.Icon,
    CashflowCategorySectionMenu,
    CashflowIncomeSectionMenu,
    CashflowTypeDetails,
  },
  mixins: [InfoPopups, CashflowTrends],
  data() {
    return {
      didExpand: false,
      iconColor: BaseUI.Colors.riseupBlack,
    };
  },
  mounted() {
    EventBus.$on(`cashflow-type-card-expanded-${this.cardTypeId}`, () => {
      this.didExpand = true;
    });
  },
  destroyed() {
    EventBus.$off(`cashflow-type-card-expanded-${this.cardTypeId}`);
  },
  props: {
    cardTypeId: {
      type: String,
      required: true,
    },
    isPreviewMode: {
      type: Boolean,
      required: true,
    },
    canEditCategory: {
      type: Boolean,
      default: true,
    },
    cardTitle: {
      type: String,
      required: true,
    },
    subTree: {
      type: Object,
      required: false,
    },
    isIncome: {
      type: Boolean,
      default: false,
    },
    cashflowCategory: {
      type: String,
      required: false,
    },
    previousMonth: {
      type: Boolean,
      default: false,
    },
    recommendedLabel: {
      type: String,
      required: false,
    },
    upToNowLabel: {
      type: String,
      required: false,
    },
    blocked: {
      type: Boolean,
      default: false,
    },
    outOfDate: {
      type: Boolean,
      default: false,
    },
    pathToCategories: {
      type: String,
      default: 'categories',
    },
    showWarningIfExceedingBudget: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('featureFlags', ['showCashflowUpdateIndicators']),
    ...mapGetters('cashflowView', ['transactionsSinceLastActivity', 'isPresentingCurrentMonth']),
    categoryForActions() {
      return this.subCategories[0];
    },
    hasBottomSlot() {
      return !!this.$slots.bottom;
    },
    isEditableCategory() {
      return !this.outOfDate && !this.previousMonth && !this.blocked && this.canEditCategory;
    },
    subCategories() {
      return this.subTree ? _.get(this.subTree, this.pathToCategories) : [];
    },
    transactionIds() {
      if (this.cardTypeId === 'savings') {
        return this.subCategories.flatMap(category => category.transactions.filter(t => t.actual)
          .map(t => t.actual.transactionId));
      }
      return this.subCategories.flatMap(category => category.transactions.map(t => t.transactionId));
    },
    hasUpdates() {
      return _.intersection(this.transactionIds, this.transactionsSinceLastActivity).length > 0;
    },
    showUpdateIndicator() {
      return this.showCashflowUpdateIndicators && this.hasUpdates && !this.didExpand;
    },
    shouldShowInfoMenuItem() {
      return this.shouldShowInfoPopups(this.previousMonth, this.blocked, this.outOfDate) && !this.shouldShowTrendsMenuItem;
    },
    shouldShowTrendsMenuItem() {
      return this.shouldShowCashflowTrend(this.cashflowCategory, this.blocked, this.outOfDate);
    },
    hasMenu() {
      return !this.isPreviewMode && (
        this.isEditableCategory
          || this.shouldShowInfoMenuItem
          || this.shouldShowTrendsMenuItem
      );
    },
    shouldDisplayIncomeMenu() {
      return this.cardTypeId === cashflowViewConsts.cashflowTypes.income && this.isPresentingCurrentMonth;
    },
  },
  methods: {
    sectionMenuOpened() {
      Segment.trackUserInteraction('OpenSectionMenuClicked', {
        trackingCategory: this.subTree ? this.subTree.categoryName : undefined,
        cardTypeId: this.cardTypeId,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../../../../../../../../node_modules/base-ui/src/scss/riseup-colors';
@import '~base-ui/src/scss/riseup-vars';
@import '../../../../../../../../../scss/mixins';

.cashflow-type-card {
  position: relative;

  .card-wrapper {
    padding-top: 18px;
    &.showing-edit-menu {
      opacity: 0.5;
    }

    &.out-of-date {
      color: $riseup_gray_2;
    }
  }

  .card-update-indicator {
    position: absolute;
    top: 8px;
    right: 6px;
    font-size: 14px;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;

    &.visible {
      opacity: 1;
    }
  }
}

</style>
